import { 
    collection, 
    doc, 
    getDocs, 
    addDoc, 
    updateDoc, 
    deleteDoc, 
    query, 
    orderBy, 
    serverTimestamp,
    getDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import type { Store, StoreCreateInput, StoreUpdateInput } from '../types/store';
import { normalizeCukcukValue } from '../utils/cukcukMapping';

const COLLECTION_NAME = 'storeLocations';
const FIRESTORE_READ_TIMEOUT_MS = 8000;

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> => {
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    const timeoutPromise = new Promise<T>((_, reject) => {
        timeoutHandle = setTimeout(() => {
            reject(new Error(errorMessage));
        }, timeoutMs);
    });

    try {
        return await Promise.race([promise, timeoutPromise]);
    } finally {
        if (timeoutHandle) {
            clearTimeout(timeoutHandle);
        }
    }
};

export const storeService = {
    // Get all stores
    getStores: async (): Promise<Store[]> => {
        try {
            const q = query(
                collection(db, COLLECTION_NAME),
                orderBy('createdAt', 'desc')
            );
            const querySnapshot = await withTimeout(
                getDocs(q),
                FIRESTORE_READ_TIMEOUT_MS,
                'Timeout reading stores from Firestore',
            );
            
            return querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    cukcukWarehouseCode: normalizeCukcukValue(data.cukcukWarehouseCode),
                    createdAt: data.createdAt?.toDate?.()?.toISOString(),
                    updatedAt: data.updatedAt?.toDate?.()?.toISOString(),
                } as Store;
            });
        } catch (error) {
            console.error('Error fetching stores:', error);
            throw new Error('Không thể tải danh sách cửa hàng');
        }
    },

    // Get active stores only
    getActiveStores: async (): Promise<Store[]> => {
        try {
            const stores = await storeService.getStores();
            return stores.filter(store => store.isActive);
        } catch (error) {
            console.error('Error fetching active stores:', error);
            throw new Error('Không thể tải danh sách cửa hàng hoạt động');
        }
    },

    // Get store by ID
    getStoreById: async (id: string): Promise<Store | null> => {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const docSnap = await withTimeout(
                getDoc(docRef),
                FIRESTORE_READ_TIMEOUT_MS,
                `Timeout reading store ${id} from Firestore`,
            );
            
            if (docSnap.exists()) {
                const data = docSnap.data();
                return {
                    id: docSnap.id,
                    ...data,
                    cukcukWarehouseCode: normalizeCukcukValue(data.cukcukWarehouseCode),
                    createdAt: data.createdAt?.toDate?.()?.toISOString(),
                    updatedAt: data.updatedAt?.toDate?.()?.toISOString(),
                } as Store;
            }
            
            return null;
        } catch (error) {
            console.error('Error fetching store:', error);
            throw new Error('Không thể tải thông tin cửa hàng');
        }
    },

    // Create new store
    createStore: async (storeData: StoreCreateInput): Promise<string> => {
        try {
            const { cukcukWarehouseCode, ...rest } = storeData;
            const docRef = await addDoc(collection(db, COLLECTION_NAME), {
                ...rest,
                ...(cukcukWarehouseCode !== undefined
                    ? { cukcukWarehouseCode: normalizeCukcukValue(cukcukWarehouseCode) }
                    : {}),
                isActive: storeData.isActive ?? true,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            
            return docRef.id;
        } catch (error) {
            console.error('Error creating store:', error);
            throw new Error('Không thể tạo cửa hàng mới');
        }
    },

    // Update store
    updateStore: async (storeData: StoreUpdateInput): Promise<void> => {
        try {
            const { id, cukcukWarehouseCode, ...updateData } = storeData;
            const docRef = doc(db, COLLECTION_NAME, id);
            
            await updateDoc(docRef, {
                ...updateData,
                ...(cukcukWarehouseCode !== undefined
                    ? { cukcukWarehouseCode: normalizeCukcukValue(cukcukWarehouseCode) }
                    : {}),
                updatedAt: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error updating store:', error);
            throw new Error('Không thể cập nhật thông tin cửa hàng');
        }
    },

    updateCukcukWarehouseCode: async (id: string, value: unknown): Promise<void> => {
        const storeId = id.trim();
        if (!storeId) throw new Error('Store id is required');

        try {
            await updateDoc(doc(db, COLLECTION_NAME, storeId), {
                cukcukWarehouseCode: normalizeCukcukValue(value),
                updatedAt: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error updating CUKCUK warehouse mapping:', error);
            throw new Error('Không thể cập nhật mã kho CUKCUK');
        }
    },

    // Delete store
    deleteStore: async (id: string): Promise<void> => {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            await deleteDoc(docRef);
        } catch (error) {
            console.error('Error deleting store:', error);
            throw new Error('Không thể xóa cửa hàng');
        }
    },

    // Toggle store active status
    toggleStoreStatus: async (id: string, isActive: boolean): Promise<void> => {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            await updateDoc(docRef, {
                isActive,
                updatedAt: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error toggling store status:', error);
            throw new Error('Không thể thay đổi trạng thái cửa hàng');
        }
    },

    // Get stores for shipping configuration (simplified format)
    getStoresForShipping: async () => {
        try {
            const stores = await storeService.getActiveStores();
            return stores.map(store => ({
                id: store.id,
                address: store.address,
                province: store.province,
                district: store.district,
                ward: store.ward,
                street: store.street,
            }));
        } catch (error) {
            console.error('Error fetching stores for shipping:', error);
            throw new Error('Không thể tải danh sách cửa hàng cho vận chuyển');
        }
    },
}; 
